import CopyLink from '@/images/CopyLink.svg';
import Email from '@/images/Email.svg';
import Facebook from '@/images/Facebook.svg';
import LinkedIn from '@/images/LinkedIn.svg';
import Twitter from '@/images/Twitter.svg';
import Whatsapp from '@/images/Whatsapp.svg';
import { useFlag } from '@/services/featureFlags';
import { copyToClipboard } from '@/utils/shareUtils';
import { ListItemIcon, Popover, PopoverProps, styled, Typography } from '@mui/material';
import { EmailShareButton, FacebookShareButton, LinkedinShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';

const ShareOptionsContainer = styled('div')`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 12px 12px;
    padding: 2rem 2rem;
    border-radius: 12px;

    &:focus {
        outline: none;
    }
`;

const ShareOptionContainer = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 16px;
    padding: 8px 16px 8px 16px;
    & img {
        transition: transform 0.2s ease-in-out;
    }

    &:hover {
        background-color: #f3f3f3;

        img {
            transition: transform 0.2s ease-in-out;
            cursor: pointer;
            transform: scale(1.1);
        }
    }
`;

const OptionLabel = styled(Typography)`
    margin-top: 1rem;
    font-weight: 400;
`;

const paperStyles = {
    borderRadius: '12px',
    boxShadow: '0px 10px 60px 0px #00000033',
    '&:focus': {
        outline: 'none',
    },
};

const ListItemIconContainer = styled(ListItemIcon)`
    display: flex;
    justify-content: center;
`;

const ListCopyIconContainer = styled(ListItemIconContainer)`
    display: contents;
    img {
        width: 75%;
    }
`;

export const socialOptions = (
    url: string,
    handleShare: (type: string) => void,
    type: 'video' | 'agent' = 'video'
): Record<string, JSX.Element> => ({
    twitter: (
        <TwitterShareButton url={url} beforeOnClick={() => handleShare('twitter')} key="twitter">
            <ShareOptionContainer>
                <ListItemIconContainer>
                    <img src={Twitter} />
                </ListItemIconContainer>
                <OptionLabel variant="h5">X (Twitter)</OptionLabel>
            </ShareOptionContainer>
        </TwitterShareButton>
    ),
    facebook: (
        <FacebookShareButton url={url} beforeOnClick={() => handleShare('facebook')} key="facebook">
            <ShareOptionContainer>
                <ListItemIconContainer>
                    <img src={Facebook} />
                </ListItemIconContainer>
                <OptionLabel variant="h5">Facebook</OptionLabel>
            </ShareOptionContainer>
        </FacebookShareButton>
    ),
    linkedIn: (
        <LinkedinShareButton url={url} beforeOnClick={() => handleShare('linkedIn')} key="linkedin">
            <ShareOptionContainer>
                <ListItemIconContainer>
                    <img src={LinkedIn} />
                </ListItemIconContainer>
                <OptionLabel variant="h5">Linkedin</OptionLabel>
            </ShareOptionContainer>
        </LinkedinShareButton>
    ),
    whatsapp: (
        <WhatsappShareButton url={url} beforeOnClick={() => handleShare('whatsapp')} key="whatsapp">
            <ShareOptionContainer>
                <ListItemIconContainer>
                    <img src={Whatsapp} />
                </ListItemIconContainer>
                <OptionLabel variant="h5">Whatsapp</OptionLabel>
            </ShareOptionContainer>
        </WhatsappShareButton>
    ),
    email: (
        <EmailShareButton
            key="email"
            url={url}
            beforeOnClick={() => handleShare('email')}
            subject={type === 'video' ? 'Check out this new creative video I made using AI' : 'Check Out My AI Agent!'}>
            <ShareOptionContainer>
                <ListItemIconContainer>
                    <img src={Email} />
                </ListItemIconContainer>
                <OptionLabel variant="h5">Email</OptionLabel>
            </ShareOptionContainer>
        </EmailShareButton>
    ),
    copy: (
        <ListCopyIconContainer
            key="copy"
            onClick={() => {
                copyToClipboard(url);
                handleShare('copy');
            }}>
            <ShareOptionContainer>
                <img src={CopyLink} />
                <OptionLabel variant="h5">Copy link</OptionLabel>
            </ShareOptionContainer>
        </ListCopyIconContainer>
    ),
});

export interface Props extends PopoverProps {
    createShareUrl: (shareOption: string) => string;
    handleShare: (shareOption: string) => void;
    testId?: string;
}

function ShareMenu({ handleShare, createShareUrl, testId, ...props }: Props) {
    return (
        <Popover {...props} data-testid={testId} PaperProps={{ sx: paperStyles }}>
            <ShareMenuContent handleShare={handleShare} createShareUrl={createShareUrl} />
        </Popover>
    );
}

type ShareMenuContentProps = Pick<Props, 'createShareUrl' | 'handleShare' | 'testId'>;

export const ShareMenuContent = ({ handleShare, createShareUrl, testId }: ShareMenuContentProps) => {
    const shareButtonSocialOptions = useFlag('social_options_share_button', Object.keys(socialOptions));
    const copyIndex = shareButtonSocialOptions.indexOf('copy');

    // Move copy link to the end of the list
    if (copyIndex !== -1) {
        shareButtonSocialOptions.splice(copyIndex, 1);
        shareButtonSocialOptions.push('copy');
    }

    return (
        <ShareOptionsContainer data-testid={testId}>
            {shareButtonSocialOptions.map(option => socialOptions(createShareUrl(option), handleShare)[option])}
        </ShareOptionsContainer>
    );
};

export default ShareMenu;
